import type { ReactNode } from 'react'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel, SunkWell } from '../../platform/primitives/Panel'
import { UNKNOWN_BANK } from './relinkOutcome'
import { relinkMatchPath } from './relinkRoutes'
import type { ProviderItem } from './useAggregator'
import { useRelinkState } from './useRelink'
import './accounts.css'

/** A reconnect that is still waiting on the user, or on the server's pairing. */
const isActive = (i: ProviderItem) => i.relinkStatus === 'pending' || i.relinkStatus === 'needs_review'

/** The new item reconnecting `oldItemId`, if one is pending or needs review. */
export function activeRelinkFor(items: ProviderItem[], oldItemId: string): ProviderItem | null {
  return items.find((i) => i.relinkedFromItemId === oldItemId && isActive(i)) ?? null
}

/** The chip that opens a reconnect's match step — on a notice, and on the
 *  disconnected bank's own card/row in place of Reconnect. A chip, never a fill
 *  (rule 9: the page's one lime fill is elsewhere). */
export function MatchAccountsLink({ newItemId }: { newItemId: string }): ReactNode {
  return (
    <ButtonLink variant="chip" size="panel" to={relinkMatchPath(newItemId)}>
      Match accounts
    </ButtonLink>
  )
}

function NeedsReviewNotice({ item }: { item: ProviderItem }) {
  const bank = item.institutionName ?? UNKNOWN_BANK
  // The count is the server's `unmatched_old`, never a guess: until it arrives, or
  // if it cannot be read, the line carries no number (and never says 0).
  const state = useRelinkState(item.id)
  const n = state.status === 'success' ? state.data.unmatchedOld.length : null
  return (
    <>
      <span className="relink-notice__text">
        {n === null
          ? `Finish reconnecting ${bank}`
          : `Finish reconnecting ${bank}: ${n} ${n === 1 ? 'account needs' : 'accounts need'} matching`}
      </span>
      <MatchAccountsLink newItemId={item.id} />
    </>
  )
}

function PendingNotice({ item }: { item: ProviderItem }) {
  const bank = item.institutionName ?? UNKNOWN_BANK
  return (
    <>
      <span className="relink-notice__text">Reconnecting {bank} — matching your accounts…</span>
      {/* The guide allows matching early; kept quiet, a text link, not a chip. */}
      <InlineLink to={relinkMatchPath(item.id)}>Match now</InlineLink>
    </>
  )
}

/**
 * "Finish reconnecting {bank}" — one row per reconnect that is `pending` or
 * `needs_review`, at the top of `/connections` and in the Institutions panel.
 * `null` when there is none. `nested` paints rows as a well, for use inside the
 * panel's own surface.
 */
export function RelinkNotices({ items, nested = false }: { items: ProviderItem[]; nested?: boolean }): ReactNode {
  const active = items.filter(isActive)
  if (active.length === 0) return null
  const Surface = nested ? SunkWell : Panel
  return (
    <ul className="relink-notices" aria-label="Reconnects in progress">
      {active.map((item) => (
        <li key={item.id}>
          <Surface className="relink-notice">
            {item.relinkStatus === 'needs_review' ? <NeedsReviewNotice item={item} /> : <PendingNotice item={item} />}
          </Surface>
        </li>
      ))}
    </ul>
  )
}
